import { Injectable } from '@angular/core';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { INoticeDialogOptions, INoticeIconSnackBarOptions, INoticeDialogData, INotifyOptions } from '../models/interfaces';
import { NotofierTemplatingService } from './notofier-templating.service';
import { NotofierUtilitiesService } from './notofier-utilities.service';

/**
 * A service for showing various types of notifications via Material style notifications, such as snackbars and dialogs.
 *
 * Note: these aren't really strategies in the sense that they contain an execute method or anything.
 */
@Injectable({ providedIn: 'root' })
export class NotofierStrategyService {
    constructor (private _utilities: NotofierUtilitiesService, private _templating: NotofierTemplatingService) {}

    /** Show a brief notification customized to deliver brief, small pieces of information, like the result of an action. */
    showDebugNotification = (message = 'Complete', config: INotifyOptions = {}): any => this._templating.openMaterialSnackbar({ message, config });

    /** Show a brief notification customized to deliver brief, small pieces of information, like the result of an action. */
    showInfoNotification = (message = 'Success!', options: INotifyOptions = {}): any => {
        const data = {
            message: message,
            icon: options.viewOptions && options.viewOptions.icon ? options.viewOptions.icon.name : 'info'
        };
        delete options.viewOptions;
        const config = { ...options, data } as INoticeIconSnackBarOptions;
        return this._templating.openIconSnackbarFromTemplate(config);
    };

    /**
	 * Show a notification with a single button that wait for a user interaction. Ideal for when you want to ensure
	 * the user sees the notification.
	 */
    showMinorNotification = (message = 'Success!', options: INotifyOptions = {}): any => {
        let actionText = 'Dismiss';
        if (options.viewOptions && options.viewOptions.controls && options.viewOptions.controls.length > 0) {
            actionText = options.viewOptions.controls[0].text || 'Dismiss';
        }
        delete options.viewOptions;
        return this._templating.openMaterialSnackbar({ message, action: actionText, config: options as MatSnackBarConfig });
    };

    /** Show a dialog style notification that blocks the screen, forcing the user to potentially choose a action of view long content. */
    showModerateNotification = (message = 'We\'re sorry, we experienced .', options: INotifyOptions = {}): any => {
        // TODO: Close any snackbars already open.

        const defaultTemplateData: INoticeDialogData = {
            text: { body: message }
        };

        const config: INoticeDialogOptions = {
            ...options,
            data: { ...defaultTemplateData, ...options.viewOptions }
        } as INoticeDialogOptions;

        return this._templating.openDialogFromTemplate(config);
    };

    /** Show a dialog style notification that blocks the screen, forcing the user to potentially choose a action of view long content. */
    showCriticalNotification = (message = 'We\'re sorry, we\'ve experienced an error.', options: INotifyOptions = {}): any => {
        // TODO: Close any snackbars already open.
        const _customData = { ...options.viewOptions };

        const defaultTemplateData: INoticeDialogData = {
            text: { title: 'Error', body: message },
            controls: [
                {
                    text: 'Dismiss',
                    class: 'notice-dialog__button mat-mdc-stroked-button mat-danger',
                    callbackFn: function (event, dialog) {
                        dialog.close();
                    }
                }
            ]
        };

        const config: INoticeDialogOptions = {
            data: { ...defaultTemplateData, ...options.viewOptions },
            panelClass: [ 'notifications-dialog--error' ]
        } as INoticeDialogOptions;

        // Ensure any icons look correct.
        if (_customData.icon) {
            config.data.icon = { name: 'error', color: 'danger', ...options.viewOptions.icon };
        }
        // Or, if there is no icon and no image, use the default.
        if (!_customData.icon && !_customData.image) {
            config.data.image = { url: 'https://cdn.orthofi.com/img/icons/of/error-red-hex.png', alt: 'Error Icon' };
        }
        // Combine any classnames
        config.panelClass = this._utilities.mergePanelClass(options.panelClass, [ 'notifications-dialog--error' ]);
        // Ensure all buttons have defaults (controls is nested, so the first destructuring doesn't help)
        config.data.controls = config.data.controls.map((control, index) => ({
            text: 'Dismiss',
            callbackFn: function (event, dialog) {
                dialog.close();
            },
            class: index < 1 ? 'notice-dialog__button mat-mdc-stroked-button mat-danger' : 'mat-mdc-softer',
            ...control
        }));
        return this._templating.openDialogFromTemplate(config);
    };
}
